import { Add, Edit, Delete, Input } from "@mui/icons-material";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useDialog, useListItemDeleteApi, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { PageTitle } from "../../layout/PageTitle";
import { createListStore } from "../../common/listStore";
import { listHeaderItemsDirect, renderListStore } from "../../common/listStoreUi";
import { useState } from "react";
import { InventoryArticleDialog } from "./InventoryArticleDialog";
import { SaleArticleDialog } from "./SaleArticleDialog";
import { SaleArticleDetailDialog } from "./SaleArticleDetailDialog";
import { createAxios } from "../../common/axios";
import { InventoryArticleDto, SaleArticleDetailDto, SaleArticleDetailProductDto, SaleArticleDto } from "./types";
import { SaleArticleDetailsImportDialog } from "./SaleArticleDetailsImportDialog";
import { distinctUntilChanged, map } from "rxjs";
import { EnumItemDto, MailProductCode } from "../../common/enum";
import { propTextField } from "../../common/entityStoreForm";
import { createEntityStore } from "../../common/entityStore";
import { applyListStoreFilters } from "../../common/listStoreFilters";
import { usePortal, useRxJsSub } from "../../common/utils";

interface InventoryArticleListFilters {
    codeAndName: string;
}

interface SaleArticleDetailListFilters {
    project: string;
}

export function ArticleListPage() {
    const [{ useSaleArticleDetailsProductsStore, useInventoryArticlesStore, useInventoryArticleListFiltersStore, useSaleArticlesStore, useSaleArticleDetailsStore, useSaleArticleDetailListFiltersStore }] = useState(() => {

        const useSaleArticleDetailsProductsStore = createListStore<SaleArticleDetailProductDto>({
            path: `articles/saleArticleDetails/getProductsAndProjects`,
        });
        const useMoProjectsStore = createListStore<EnumItemDto>({
            data: useSaleArticleDetailsProductsStore.getState().items$.pipe(map(products => ({
                items: (products.items.find(p => p.code === MailProductCode)?.projects || [])
                    .map(p => ({ id: p.id, name: p.codeAndName })),
                isFetching: products.isFetching
            })))
        });

        const useInventoryArticlesStore = createListStore<InventoryArticleDto>({
            path: "articles/inventoryArticles",
            itemsPerPage: 10,
            getId: p => p.id,
            listProps: [
                { column: "code", label: "Article", width: 80 },
                { column: "nme", label: "Description", width: 350 },
                { column: "isPost", label: "Pst?", width: 50, type: "bool" },
                { column: "shp", label: "SHP", width: 100, type: "number" },
                { column: "ret", label: "RET", width: 100, type: "number" },
                { column: "bat", label: "BAT", width: 100, type: "number" },
                { column: "dtd", label: "DTD", width: 100, type: "number" },
                { column: "wt", label: "Weight [g]", width: 100, type: "number" },
                { column: "origCountry", label: "Country of origin", width: 120 },
                { column: "tarCode", label: "Tariff Code", width: 120 },
                { column: "status", label: "Status", width: 120 },
                { column: "isPackage", label: "Is Package", width: 100, type: "bool" },
            ],
        });
        const useInventoryArticleListFiltersStore = createEntityStore<InventoryArticleListFilters>({
            data: { codeAndName: "" },
        });

        const useSaleArticlesStore = createListStore<SaleArticleDto>({
            data: useInventoryArticlesStore.getState().selectedItem$.pipe(map(p => ({ items: p?.saleArticles || [], isFetching: false }))),
            itemsPerPage: 5,
            getId: p => p.id,
            listProps: [
                { column: "code", label: "Article", width: 80 },
                { column: "name", label: "Description", width: 350 },
                { column: "price", label: "Price", width: 100, type: "currency" },
                { column: "baseQuantity", label: "Base qty", width: 80, type: "number" },
                { column: "vatPercent", label: "VAT [%]", width: 90, type: "number" },
                { column: "moVatPercent", label: "MO VAT [%]", width: 90, type: "number" },
                { column: "moArticle", label: "MO?", width: 50, type: "bool" },
            ],
            autoSelectFirstItem: true,
        })

        const useSaleArticleDetailsStore = createListStore<SaleArticleDetailDto>({
            itemsPerPage: 5,
            getId: p => p.id,
            listProps: [
                { column: "projectId", label: "Project", flex: 1, enumStore: useMoProjectsStore },
                { column: "price", label: "Price", width: 100, type: "currency" },
                { column: "useDiscount", label: "Qty disc?", width: 80, type: "bool" },
                { column: "packageItems", label: "Is package?", width: 90, type: "bool", valueGetter: p => p.row.packageItems.length > 0 },
            ],
        });
        const useSaleArticleDetailListFiltersStore = createEntityStore<SaleArticleDetailListFilters>({
            data: { project: "" },
        });

        return { useSaleArticleDetailsProductsStore, useInventoryArticlesStore, useInventoryArticleListFiltersStore, useSaleArticlesStore, useSaleArticleDetailsStore, useSaleArticleDetailListFiltersStore };
    });

    useRxJsSub(() => useSaleArticlesStore.getState().selectedItem$.pipe(map(p => p?.id), distinctUntilChanged()).subscribe(id => {
        useSaleArticleDetailsStore.getState().setPath(id == null ? undefined : `articles/saleArticleDetails?saleArticleId=${id}`);
        useSaleArticleDetailsStore.getState().fetch();
    }));
    useRxJsSub(() => applyListStoreFilters(useInventoryArticleListFiltersStore, useInventoryArticlesStore, [
        { listProps: ["codeAndName"], operator: "Like", filterProp: "codeAndName" },
    ], true));
    useRxJsSub(() => applyListStoreFilters(useSaleArticleDetailListFiltersStore, useSaleArticleDetailsStore, [
        { listProps: ["projectName"], operator: "Like", filterProp: "project" },
    ], true));

    const inventoryArticlesStore = useInventoryArticlesStore(p => ({ selectedItem: p.selectedItem }));
    const newInventoryArticleDialog = useDialog(close => <InventoryArticleDialog close={close} useArticlesStore={useInventoryArticlesStore} article={null}
        onChange={() => { useInventoryArticlesStore.getState().fetch(); }} />, "sm");
    const editInventoryArticleDialog = useDialog(close => <InventoryArticleDialog close={close} useArticlesStore={useInventoryArticlesStore} article={inventoryArticlesStore.selectedItem!}
        onChange={() => { useInventoryArticlesStore.getState().fetch(); }} />, "sm");
    const simpleDialog = useSimpleDialog();
    const deleteInventoryArticle = useListItemDeleteApi(useInventoryArticlesStore, item => "articles/inventoryArticles/" + item.id);

    const saleArticles = useSaleArticlesStore(p => ({ selectedItem: p.selectedItem }));
    const newSaleArticleDialog = useDialog(close => <SaleArticleDialog close={close} saleArticle={null} inventoryArticle={inventoryArticlesStore.selectedItem!}
        onChange={() => {
            useInventoryArticlesStore.getState().fetch();
        }} />, "xs");
    const editSaleArticleDialog = useDialog(close => <SaleArticleDialog close={close} saleArticle={saleArticles.selectedItem!} inventoryArticle={inventoryArticlesStore.selectedItem!}
        onChange={() => {
            useInventoryArticlesStore.getState().fetch();
        }} />, "xs");
    async function deleteSaleArticle() {
        await simpleDialog.show("Delete sales article", "Delete selected sales article?", undefined, yesNoButtons(async () => {
            try {
                await createAxios().delete(`articles/saleArticles/${saleArticles.selectedItem?.id}`);
            } finally {
                useInventoryArticlesStore.getState().fetch();
            }
        }));
    }

    const saleArticleDetails = useSaleArticleDetailsStore(p => ({ selectedItem: p.selectedItem }));
    const newSaleArticleDetailDialog = useDialog((close, dialogProps) => <SaleArticleDetailDialog close={close} dialogProps={dialogProps} saleArticleDetail={null}
        inventoryArticle={inventoryArticlesStore.selectedItem!} useInventoryArticlesStore={useInventoryArticlesStore}
        saleArticle={saleArticles.selectedItem!} useProductsStore={useSaleArticleDetailsProductsStore} useSaleArticleDetailsStore={useSaleArticleDetailsStore}
        onChange={() => { useSaleArticleDetailsStore.getState().fetch(); }} />, undefined, { usingCustomDialogTag: true });
    const editSaleArticleDetailDialog = useDialog((close, dialogProps) => <SaleArticleDetailDialog close={close} dialogProps={dialogProps} saleArticleDetail={saleArticleDetails.selectedItem!}
        inventoryArticle={inventoryArticlesStore.selectedItem!} useInventoryArticlesStore={useInventoryArticlesStore}
        saleArticle={saleArticles.selectedItem!} useProductsStore={useSaleArticleDetailsProductsStore} useSaleArticleDetailsStore={useSaleArticleDetailsStore}
        onChange={() => { useSaleArticleDetailsStore.getState().fetch(); }} />, undefined, { usingCustomDialogTag: true });

    const saleArticleDetailsImportDialog = useDialog(close => <SaleArticleDetailsImportDialog close={close}
        onChange={() => { useSaleArticleDetailsStore.getState().fetch(); }} />, "sm");

    async function deleteSaleArticleDetail() {
        await simpleDialog.show("Delete sales detail", "Delete selected sales detail?", undefined, yesNoButtons(async () => {
            try {
                await createAxios().delete(`articles/saleArticleDetails/${saleArticleDetails?.selectedItem?.id}`);
            } finally {
                useSaleArticleDetailsStore.getState().fetch();
            }
        }));
    }

    const inventoryArticlesHeader = usePortal(listHeaderItemsDirect([
        ["searchCodeAndName", () => <Box width={420}>{propTextField(useInventoryArticleListFiltersStore)("codeAndName", "Code and description", { inListHeader: true })}</Box>],
        ["edit", () => <Button color="edit" disabled={inventoryArticlesStore.selectedItem == null} title="Edit" onClick={() => editInventoryArticleDialog.show()}><Edit /></Button>],
        ["add", () => <Button color="add" title="Add" onClick={() => newInventoryArticleDialog.show()}><Add /></Button>],
        ["remove", () => <Button color="delete" disabled={inventoryArticlesStore.selectedItem == null} title="Delete" onClick={deleteInventoryArticle.showDialog}><Delete /></Button>],
        ["import", () => <Button title="Import sales article details from CSV" onClick={() => saleArticleDetailsImportDialog.show()}><Input /></Button>]
    ]));

    const salesArticlesHeader = usePortal(listHeaderItemsDirect([
        ["edit", () => <Button color="edit" disabled={saleArticles.selectedItem == null} title="Edit" onClick={() => editSaleArticleDialog.show()}><Edit /></Button>],
        ["add", () => <Button color="add" title="Add" disabled={inventoryArticlesStore.selectedItem == null} onClick={() => newSaleArticleDialog.show()}><Add /></Button>],
        ["remove", () => <Button color="delete" disabled={saleArticles.selectedItem == null} title="Delete" onClick={deleteSaleArticle}><Delete /></Button>],
    ]));

    const salesArticleDetailsHeader = usePortal(listHeaderItemsDirect([
        ["searchProject", () => <Box width={250}>{propTextField(useSaleArticleDetailListFiltersStore)("project", "Project", { inListHeader: true })}</Box>],
        ["edit", () => <Button color="edit" disabled={saleArticleDetails.selectedItem == null} title="Edit" onClick={() => { editSaleArticleDetailDialog.show(); }}><Edit /></Button>],
        ["add", () => <Button color="add" title="Add" disabled={saleArticles.selectedItem == null} onClick={() => { newSaleArticleDetailDialog.show(); }}><Add /></Button>],
        ["remove", () => <Button color="delete" disabled={saleArticleDetails.selectedItem == null} title="Delete" onClick={deleteSaleArticleDetail}><Delete /></Button>],
    ]));

    return <>
        {inventoryArticlesHeader.render()}
        {salesArticlesHeader.render()}
        {salesArticleDetailsHeader.render()}
        {newInventoryArticleDialog.render()}
        {editInventoryArticleDialog.render()}
        {simpleDialog.render()}
        {saleArticleDetailsImportDialog.render()}
        {deleteInventoryArticle.dialog.render()}
        <PageTitle title="Inventory articles and packages" documentTitle="Articles" />
        {renderListStore(useInventoryArticlesStore, {
            rowHeight: 32,
            onDoubleClick: () => {
                editInventoryArticleDialog.show();
            },
            header: inventoryArticlesHeader.portal,
        })}

        <Grid container spacing={2}>
            <Grid item xs={7}>
                {newSaleArticleDialog.render()}
                {editSaleArticleDialog.render()}
                <Typography variant="h5" mt={2}>Sales articles</Typography>
                {renderListStore(useSaleArticlesStore, {
                    rowHeight: 32,
                    onDoubleClick: () => {
                        editSaleArticleDialog.show();
                    },
                    header: salesArticlesHeader.portal,
                })}
            </Grid>
            <Grid item xs={5}>
                {newSaleArticleDetailDialog.render()}
                {editSaleArticleDetailDialog.render()}
                <Typography variant="h5" mt={2}>Sales details</Typography>
                {renderListStore(useSaleArticleDetailsStore, {
                    rowHeight: 32,
                    onDoubleClick: () => {
                        editSaleArticleDetailDialog.show();
                    }, header: salesArticleDetailsHeader.portal,
                })}
            </Grid>
        </Grid>
    </>;
}
